import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { TExtendedApplication } from 'types/application.types';
import type { TAnyClientQuestionType } from '@hubertai/screening-question-types';

type TJobApplicationDialogData = {
  show: boolean;
  data?: {
    jobId: string;
    applicationId: string;
    currentRow: TExtendedApplication;
    rows: TExtendedApplication[];
  };
};

type TEditQuestionDialog = {
  show: boolean;
  data: {
    question: TAnyClientQuestionType;
  } | null;
};

type TEditQuestionInTemplate = {
  show: boolean;
  data: {
    nodeId: string;
    question: TAnyClientQuestionType;
    language: string;
  } | null;
};

type TQuestionRules = {
  show: boolean;
  data: {
    templateId: string;
    nodeId: string;
  } | null;
};

type TConfirmationDialogLayoutControl = {
  show: boolean;
  data: {
    title: string;
    message: string;
    onConfirm: {
      buttonText?: string;
      action: string;
    };
    onCancel: {
      buttonText?: string;
      action: string;
    };
  } | null;
};

type TGeneralSearchDialogLayoutControl = {
  show: boolean;
};

type TLayoutState = {
  refetchJobApplicationsTableData: boolean;
  refetchJobCardsData: boolean;
  showDisabledOptions: boolean;
  showOptionReferences: boolean;
  editQuestionDialog: TEditQuestionDialog;
  editQuestionInTemplate: TEditQuestionInTemplate;
  questionRules: TQuestionRules;
  createTemplateDialog: {
    show: boolean;
    data: any;
  };
  createClientDialog: {
    show: boolean;
  };
  jobApplicationDialog: TJobApplicationDialogData;
  confirmationDialog: TConfirmationDialogLayoutControl;
  generalSearchDialog: TGeneralSearchDialogLayoutControl;
};

const initialState: TLayoutState = {
  refetchJobApplicationsTableData: false,
  refetchJobCardsData: false,
  showDisabledOptions: false,
  showOptionReferences: false,
  editQuestionDialog: {
    show: false,
    data: null,
  },
  editQuestionInTemplate: {
    show: false,
    data: null,
  },
  questionRules: {
    show: false,
    data: null,
  },
  createTemplateDialog: {
    show: false,
    data: null,
  },
  createClientDialog: {
    show: false,
  },
  jobApplicationDialog: {
    show: false,
  },
  confirmationDialog: {
    show: false,
    data: null,
  },
  generalSearchDialog: {
    show: false,
  },
  /*
  searchQuestionDialog: {
    show: false,
    data: null,
    featureFlags: null
  },
  updateQuestionDialog: {
    show: false,
    qutter_id: null,
    language: null,
    newLanguage: false
  },
  nodeActionsDialog: {
    show: false,
    data: null
  },
  formEvaluationDialog: {
    show: false,
    data: null,
  },
  questionCommentDialog: {
    show: false,
    data: null,
  }
  */
};

const LayoutControlSlice = createSlice({
  name: 'layout',
  initialState,
  reducers: {
    // toggleSearchQuestionDialog: (state: any, action: any) => {
    //   const { show, data } = action.payload;
    //   console.log('show', show)
    //   console.log('data', data)
    //   return {
    //     ...state,
    //     searchQuestionDialog: {
    //       show: show,
    //       data: data
    //     }
    //   };
    // },

    // toggleQuestionRulesDialog: (state: any, action: any) => {
    //   const { show, data } = action.payload;
    //   return {
    //     ...state,
    //     questionRulesDialog: {
    //       show: show,
    //       data: data
    //     }
    //   };
    // },

    toggleRefetchJobTabCards: (
      state: any,
      action: PayloadAction<{
        refetch: boolean;
      }>,
    ) => {
      const { refetch } = action.payload;
      return {
        ...state,
        refetchJobCardsData: refetch,
      };
    },

    toggleRefetchJobApplicationsTableData: (
      state: any,
      action: PayloadAction<{
        refetch: boolean;
      }>,
    ) => {
      const { refetch } = action.payload;
      return {
        ...state,
        refetchJobApplicationsTableData: refetch,
      };
    },

    toggleEditQuestionDialog: (state: any, action: PayloadAction<TEditQuestionDialog>) => {
      const { show, data } = action.payload;
      return {
        ...state,
        editQuestionDialog: {
          show: show,
          data: data,
        },
      };
    },

    toggleEditQuestionInTemplate: (state: any, action: PayloadAction<TEditQuestionInTemplate>) => {
      const { show, data } = action.payload;
      return {
        ...state,
        editQuestionInTemplate: {
          show: show,
          data: data,
        },
      };
    },

    toggleQuestionRules: (state: any, action: PayloadAction<TQuestionRules>) => {
      const { show, data } = action.payload;
      return {
        ...state,
        questionRules: {
          show: show,
          data: data,
        },
      };
    },

    toggleCreateTemplateDialog: (
      state: any,
      action: PayloadAction<{
        show: boolean;
        data?: any;
      }>,
    ) => {
      const { show, data } = action.payload;
      return {
        ...state,
        createTemplateDialog: {
          show: show,
          data: data,
        },
      };
    },

    toggleCreateClientDialog: (state: TLayoutState, action: PayloadAction<{ show: boolean }>) => {
      return {
        ...state,
        createClientDialog: { show: action.payload.show },
      };
    },

    toggleJobApplicationDialog: (
      state: any,
      action: PayloadAction<{
        show: boolean;
        data?: {
          jobId: string;
          applicationId: string;
          currentRow: TExtendedApplication;
          rows: TExtendedApplication[];
        };
      }>,
    ) => {
      const { show, data } = action.payload;
      return {
        ...state,
        jobApplicationDialog: {
          show: show,
          data: data,
        },
      };
    },

    toggleConfirmationDialog: (
      state: any,
      action: PayloadAction<{
        show: boolean;
        data?: TConfirmationDialogLayoutControl['data'];
      }>,
    ) => {
      const { show, data } = action.payload;
      return {
        ...state,
        confirmationDialog: {
          show: show,
          data: data,
        },
      };
    },

    setShowDisabledOptions: (state: TLayoutState, action: PayloadAction<boolean>) => {
      state.showDisabledOptions = action.payload;
    },

    setShowOptionReferences: (state: TLayoutState, action: PayloadAction<boolean>) => {
      state.showOptionReferences = action.payload;
    },
  },
});

export const {
  toggleRefetchJobApplicationsTableData,
  toggleRefetchJobTabCards,
  toggleEditQuestionDialog,
  toggleEditQuestionInTemplate,
  toggleQuestionRules,
  toggleCreateTemplateDialog,
  toggleCreateClientDialog,
  toggleJobApplicationDialog,
  setShowDisabledOptions,
  setShowOptionReferences,
} = LayoutControlSlice.actions;
export default LayoutControlSlice.reducer;
